import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { KeenIcon } from '@/components'
import { toAbsoluteUrl } from '@/utils/Assets'
import { formatDate } from '@/pages/buddy/utils/Date'
import { getFileIcon } from '@/pages/buddy/utils/Filetype'
import { DeleteFileModal } from '@/components/file/DeleteFileModal.tsx'
import { enqueueSnackbar } from 'notistack'

interface IFileItem {
    p_id: string
    original_name: string
    file_type: string
    download_url: string
    created_at: string
}

interface IFilesProps {
    title: string
    items: IFileItem[]
}

export const Files = ({ title, items }: IFilesProps) => {
    // Maintain a local copy of the file list
    const [fileList, setFileList] = useState<IFileItem[]>(items)
    // For deletion confirmation
    const [selectedFile, setSelectedFile] = useState<IFileItem | null>(null)
    const [isModalOpen, setModalOpen] = useState(false)

    /**
     * Sync the local state with the incoming prop whenever `items` changes.
     */
    useEffect(() => {
        setFileList(items)
    }, [items])

    /**
     * Download a file from the server and trigger a browser download.
     */
    const handleFileDownload = async (url: string, fileName: string) => {
        try {
            const fullUrl = `${import.meta.env.VITE_APP_API_URL}${url}`
            const token = localStorage.getItem('authToken')

            const response = await axios.get(fullUrl, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            })

            const blob = new Blob([response.data])
            const downloadLink = document.createElement('a')
            downloadLink.href = URL.createObjectURL(blob)
            downloadLink.download = fileName
            downloadLink.click()
            URL.revokeObjectURL(downloadLink.href)
        } catch (error) {
            console.error('Download error:', error)
            enqueueSnackbar('Failed to download the file.', {
                variant: 'solid',
                state: 'danger',
            })
        }
    }

    /**
     * Prepare a file for deletion, then open a confirmation modal.
     */
    const handleDeleteClick = (file: IFileItem) => {
        setSelectedFile(file)
        setModalOpen(true)
    }

    /**
     * Confirm deletion via API, and remove the deleted file from the local state.
     */
    const handleConfirmDelete = async () => {
        if (!selectedFile) return

        try {
            const token = localStorage.getItem('authToken')
            await axios.delete(`${import.meta.env.VITE_APP_API_URL}/file/${selectedFile.p_id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            })

            enqueueSnackbar('The file has been successfully deleted.', {
                variant: 'solid',
                state: 'success',
            })

            // Remove the file from local state
            setFileList((prevFiles) => prevFiles.filter((f) => f.p_id !== selectedFile.p_id))
        } catch (error) {
            enqueueSnackbar('Failed to delete the file.', {
                variant: 'solid',
                state: 'danger',
            })
        } finally {
            setModalOpen(false)
            setSelectedFile(null)
        }
    }

    /**
     * Render each file, including its icon and download/delete actions.
     */
    const renderFileItem = (item: IFileItem, index: number) => {
        const fileIcon = getFileIcon(item.file_type)

        return (
            <div key={index} className="flex items-center gap-3">
                {/* Icon and metadata */}
                <div className="flex items-center grow gap-2.5">
                    <img
                        src={toAbsoluteUrl(`/media/file-types/${fileIcon}`)}
                        alt="file type icon"
                    />
                    <div className="flex flex-col">
            <span
                className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary mb-px"
                onClick={() => handleFileDownload(item.download_url, item.original_name)}
            >
              {item.original_name}
            </span>
                        <span className="text-xs text-gray-700">
              {formatDate(item.created_at)}
            </span>
                    </div>
                </div>

                {/* Action buttons: Download and Delete */}
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-icon btn-light btn-clear"
                        onClick={() => handleFileDownload(item.download_url, item.original_name)}
                    >
                        <KeenIcon icon="file-down" />
                    </button>
                    <button
                        className="btn btn-sm btn-icon btn-light btn-clear"
                        onClick={() => handleDeleteClick(item)}
                    >
                        <KeenIcon icon="trash" />
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">{title}</h3>
            </div>

            <div className="card-body">
                <div className="grid gap-2.5 lg:gap-5">
                    {fileList.length > 0 ? (
                        fileList.map((item, index) => renderFileItem(item, index))
                    ) : (
                        <p className="text-sm text-gray-600">No Files available.</p>
                    )}
                </div>
            </div>

            {/* Modal for confirming file deletion */}
            {selectedFile && (
                <DeleteFileModal
                    open={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                    fileName={selectedFile.original_name}
                />
            )}
        </div>
    )
}
